function generateCodeVerifier(length = 128) {
    const bytes = window.crypto.getRandomValues(new Uint8Array(length / 2));
    return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
}

async function generateCodeChallenge(codeVerifier) {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
}

function getSpotifyRedirectUri(location = window.location) {
    if (location.protocol !== 'https:' &&
        !['localhost', '127.0.0.1', '[::1]'].includes(location.hostname)) {
        throw new Error('Spotify login requires HTTPS or a local loopback address. Open the app on HTTPS or http://127.0.0.1.');
    }
    const url = new URL('/callback', location.origin);
    if (url.hostname === 'localhost') url.hostname = '127.0.0.1';
    return url.href;
}

async function createSpotifyLoginUrl(clientId, redirectUri) {
    if (!window.crypto?.subtle) {
        throw new Error('Secure browser cryptography is unavailable. Open the app using HTTPS or http://127.0.0.1.');
    }
    const verifier = generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    const state = generateCodeVerifier(64);
    sessionStorage.setItem('spotify_verifier', verifier);
    sessionStorage.setItem('spotify_state', state);
    sessionStorage.setItem('spotify_redirect_uri', redirectUri);
    const params = new URLSearchParams({
        response_type: 'code',
        client_id: clientId,
        scope: 'playlist-read-private playlist-read-collaborative playlist-modify-private playlist-modify-public user-read-private user-read-email',
        redirect_uri: redirectUri,
        code_challenge_method: 'S256',
        code_challenge: challenge,
        state,
    });
    return `https://accounts.spotify.com/authorize?${params}`;
}

async function completeSpotifyLogin(clientId, params) {
    const state = sessionStorage.getItem('spotify_state');
    const verifier = sessionStorage.getItem('spotify_verifier');
    const redirectUri = sessionStorage.getItem('spotify_redirect_uri');
    try {
        if (!state || params.get('state') !== state || !verifier || !redirectUri) {
            throw new Error('Spotify login could not be verified in this browser. Please connect again from this page.');
        }
        if (params.has('error')) {
            throw new Error(`Spotify authorization failed: ${params.get('error')}. Please connect again.`);
        }
        const response = await fetch('https://accounts.spotify.com/api/token', {
            method: 'POST',
            headers: {'Content-Type': 'application/x-www-form-urlencoded'},
            body: new URLSearchParams({
                client_id: clientId,
                grant_type: 'authorization_code',
                code: params.get('code'),
                redirect_uri: redirectUri,
                code_verifier: verifier,
            }),
        });
        const data = await response.json();
        if (!response.ok || typeof data.access_token !== 'string' || !data.access_token) {
            throw new Error(`Spotify token exchange failed: ${data.error_description || data.error || `HTTP ${response.status}`}. Please connect again.`);
        }
        return data.access_token;
    } finally {
        sessionStorage.removeItem('spotify_verifier');
        sessionStorage.removeItem('spotify_state');
        sessionStorage.removeItem('spotify_redirect_uri');
    }
}

export {generateCodeVerifier, generateCodeChallenge, getSpotifyRedirectUri, createSpotifyLoginUrl, completeSpotifyLogin};